import { useEffect, useState } from 'react';
import {
  getExternalEmrImportReadiness,
  type ExternalEmrImportReadiness,
} from '../services/externalEmrPatients';

/**
 * Whether the agency's stored External EMR login and consent can carry an
 * import now, read once per page view.
 *
 * The shell chart page shows the reason before anyone presses Import, so the
 * read happens on mount, not on press. Surfaces on one page share one request:
 * a result is reused for a minute, and an in-flight request is joined. A failed read counts as ready, the same
 * way the server treats an unreachable Roger Docs; the press itself still
 * answers with the reason.
 */
export const READINESS_REUSE_MS = 60_000;

const READY: ExternalEmrImportReadiness = {
  import_ready: true,
  import_blocked_reason: null,
};

let cached: { at: number; value: ExternalEmrImportReadiness } | null = null;
let inFlight: Promise<ExternalEmrImportReadiness> | null = null;

function readReadiness(): Promise<ExternalEmrImportReadiness> {
  if (cached && Date.now() - cached.at < READINESS_REUSE_MS) {
    return Promise.resolve(cached.value);
  }
  if (!inFlight) {
    inFlight = getExternalEmrImportReadiness()
      .then(value => {
        cached = { at: Date.now(), value };
        return value;
      })
      .catch((error: unknown) => {
        console.error('Failed to load External EMR import readiness:', error);
        return READY;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/** Tests only: forget the shared result. */
export function resetExternalEmrImportReadiness() {
  cached = null;
  inFlight = null;
}

/**
 * `enabled` is false for agencies or users who cannot start an import, so
 * they make no request. Until the read answers, `loaded` is false: callers
 * show neither Import nor a reason, so Import never flashes before a reason
 * replaces it.
 */
export function useExternalEmrImportReadiness({
  enabled,
}: {
  enabled: boolean;
}): { ready: boolean; blockedReason: string | null; loaded: boolean } {
  const [readiness, setReadiness] = useState<ExternalEmrImportReadiness | null>(
    null
  );
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    void readReadiness().then(value => {
      if (alive) setReadiness(value);
    });
    return () => {
      alive = false;
    };
  }, [enabled]);
  if (!enabled || readiness === null) {
    return { ready: true, blockedReason: null, loaded: !enabled };
  }
  return {
    ready: readiness.import_ready,
    blockedReason: readiness.import_ready
      ? null
      : (readiness.import_blocked_reason ?? null),
    loaded: true,
  };
}
